import { useState } from 'react';

import { useUserInfo } from '@domain/user/useUserInfo';
import { PostViewOptions } from '../../constants/constants';
import { Post } from '../../types/Post';
import { PostDetailsView } from '../PostDetailsView/PostDetailsView';
import { PostLikesView } from '../PostLikesView/PostLikesView';
import { PostCommentsView } from '../PostsViewerTouchDevice/PostCommentsView/PostCommentsView';
import { PostItem } from '../PostsViewerTouchDevice/PostsListView/PostsList/PostItem/PostItem';
import { RegisterWallView } from '../RegisterWallView/RegisterWallView';
import { getDisplayUserDetails } from '../../helpers/getDisplayUserDetails';

interface SinglePostListItemProps {
  post: Post;
  className?: string;
  onNotLoggedIn?: () => void;
}

/**
 * Component for displaying a single post in an instagram style list view.
 *
 * @component
 * @param {SinglePostListItemProps} props - The properties for the component.
 * @param {Catch} props.catchOrPost - The catch or post data to display.
 * @param {string} props.currentCatchPostId - The ID of the current catch or post.
 * @param {'catch' | 'post'} props.type - The type of the item, either 'catch' or 'post'.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @remarks
 * This component shows the same view as the PostViewerTouchDevice component but without the full screen overlay.
 * It can be used to show a single post or it can be looped to show a list of posts. There is no logic to change
 * the url based on the post viewer being open or the currently viewed post.
 *
 * Mainly used to show the mobile list view on desktop devices.
 *
 */
export const SinglePostListItem = ({ post, className, onNotLoggedIn }: SinglePostListItemProps) => {
  const [selectedView, setSelectedView] = useState<PostViewOptions>('postDetails');
  const { isLoggedIn } = useUserInfo();
  const scrollSelector = selectedView === 'postDetailsWithScroll' ? '#post-products' : undefined;
  const hasCatch = !!post.catch;
  const postId = post._id;
  const posts = [post];
  const postUser = post.user;
  const displayEntity = post.displayEntity;
  const displayUserDetails = getDisplayUserDetails(
    displayEntity,
    postUser._id,
    postUser.nickname,
    postUser.avatar?.url ?? '',
  );

  const isPostCommentsView = selectedView === 'postComments';
  const isPostDetailsView = selectedView.includes('postDetails');
  const isPostLikesView = selectedView === 'postLikes' && postId;
  const isPostAuthorDataAvailable =
    !!displayUserDetails.displayUserNickname && !!displayUserDetails.displayUserAvatarUrl;
  const isRegisterWallView = selectedView === 'registerWallView' && isPostAuthorDataAvailable;
  const [isPostViewerOpen, setIsPostViewerOpen] = useState(false);

  const handleNotLoggedIn = (): void => {
    if (onNotLoggedIn) {
      onNotLoggedIn();
    } else {
      setSelectedView('registerWallView');
      setIsPostViewerOpen(true);
    }
  };

  const handleBackButtonClick = (): void => {
    setIsPostViewerOpen(false);
  };

  const handleViewChange = (viewName: PostViewOptions): void => {
    if (!isLoggedIn) {
      (() => {
        handleNotLoggedIn();
      })();
      return;
    }

    (() => {
      setSelectedView(viewName);
      setIsPostViewerOpen(true);
    })();
  };

  return (
    <div data-testid={`SinglePostListItem-${postId}`} className={className}>
      {isPostViewerOpen ? (
        <div className="">
          {isPostCommentsView && (
            <PostCommentsView
              posts={posts}
              currentPostId={postId}
              className="col-start-4 col-end-6 row-start-1 rounded-r-lg"
              onBackButtonClick={handleBackButtonClick}
              onViewChange={handleViewChange}
            />
          )}
          {isPostDetailsView && (
            <PostDetailsView
              currentPostId={postId}
              className="col-start-4 col-end-6 row-start-1 rounded-r-lg"
              onBackButtonClick={handleBackButtonClick}
              scrollSelector={scrollSelector}
              hasCatch={hasCatch}
            />
          )}
          {isPostLikesView && (
            <PostLikesView currentPostId={postId} onBackButtonClick={handleBackButtonClick} />
          )}
          {isRegisterWallView && (
            <RegisterWallView
              onBackButtonClick={handleBackButtonClick}
              userAvatarUrl={displayUserDetails.displayUserAvatarUrl}
              userNickname={displayUserDetails.displayUserNickname}
            />
          )}
        </div>
      ) : (
        <PostItem post={post} onViewChange={handleViewChange} />
      )}
    </div>
  );
};
